import { LabelHTMLAttributes } from 'react';
import { Icon, IconName } from '../../Graphics';
import { RequiredIndicator } from '../RequiredIndicator';
import { ScreenReaderOnly } from '../../ScreenReaderOnly';
import { Tooltip } from '../../Tooltip';
import { joinClassNames } from '../../../utils';
import styles from './styles.module.scss';
import { Place } from 'react-tooltip';

type LabelTooltipProps = { id: string; text: string; tooltipPlace?: Place };

const LabelTooltip = ({ id, text, tooltipPlace }: LabelTooltipProps) => (
  <Tooltip tooltipId={`${id}__tooltip`} text={text} className={styles.tooltip} place={tooltipPlace}>
    <Icon name={IconName.Help} className={styles.tooltipIcon} />
  </Tooltip>
);

export type LabelProps = Omit<LabelHTMLAttributes<HTMLLabelElement>, 'htmlFor'> & {
  htmlFor: string;
  isRequired?: boolean;
  tooltip?: string;
  tooltipPlace?: Place;
};

/**
 * Not exported to the entrypoint - only to be used for building other components.
 */
export const Label = ({
  children,
  htmlFor,
  isRequired = false,
  tooltip,
  hidden,
  tooltipPlace,
  className,
  ...props
}: LabelProps) => {
  const RenderedLabel = () => (
    <label className={joinClassNames(styles.label, className)} htmlFor={htmlFor} {...props}>
      {children}
      {isRequired && <RequiredIndicator className={styles.requiredIndicator} />}
      {tooltip && <LabelTooltip id={htmlFor} text={tooltip} tooltipPlace={tooltipPlace} />}
    </label>
  );

  if (hidden) {
    return (
      <ScreenReaderOnly>
        <RenderedLabel />
      </ScreenReaderOnly>
    );
  }

  return <RenderedLabel />;
};
